import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const PUBLIC_MANIFEST = {
  href: '/manifest.json',
  title: 'Bounce Dads ATL',
  themeColor: '#1e3a5f',
  statusBarStyle: 'default'
};

const ADMIN_MANIFEST = {
  href: '/admin-manifest.json',
  title: 'BD Admin',
  themeColor: '#111827',
  statusBarStyle: 'black-translucent'
};

const setMetaContent = (selector: string, content: string) => {
  const tag = document.querySelector<HTMLMetaElement>(selector);
  if (tag) {
    tag.content = content;
  }
};

/**
 * Keeps the manifest link pointing at the right app as the user navigates.
 *
 * iOS 16.4+ reads start_url out of the manifest instead of bookmarking the page
 * you are looking at, so a single site-wide manifest means an icon saved from
 * /admin/bookings still launches the public home page.
 *
 * This hook is NOT what fixes that on iOS. Safari reads the manifest while
 * parsing the document, which is long before React mounts, and it does not
 * re-read when the <link> is replaced afterwards. The initial choice is made by
 * the inline script in public/index.html; keep the two in step. This hook
 * covers client-side navigation for browsers that do re-read, and keeps the
 * theme color and Apple meta tags matching the section you are in.
 */
export const useAppManifest = () => {
  const location = useLocation();

  useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin');
    const config = isAdmin ? ADMIN_MANIFEST : PUBLIC_MANIFEST;

    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    // Re-pointing href alone can be ignored by Safari, so replace the node.
    if (link && !link.href.endsWith(config.href)) {
      const replacement = link.cloneNode(true) as HTMLLinkElement;
      replacement.href = config.href;
      link.replaceWith(replacement);
    }

    setMetaContent('meta[name="apple-mobile-web-app-title"]', config.title);
    setMetaContent('meta[name="apple-mobile-web-app-status-bar-style"]', config.statusBarStyle);
    setMetaContent('meta[name="theme-color"]', config.themeColor);
  }, [location.pathname]);
};
